import { AiQuestionStatus } from '../api/payment/ai-question.api';

// Hàm thuần cho "Hỏi đáp AI trên live" — nhãn trạng thái, chọn câu kế tiếp chưa phát trên
// overlay, dựng câu đọc to gộp người hỏi/câu hỏi/câu trả lời. Tách khỏi component để test
// không phải dựng React.

/** Câu hỏi sau khi trim tối đa bấy nhiêu ký tự (khớp giới hạn payment-service). */
export const AI_QUESTION_MAX = 200;

/** Trạng thái một câu hỏi ⇒ nhãn tiếng Việt hiện trên khối người xem / bảng điều khiển. */
export const questionStatusLabel = (s: AiQuestionStatus): string => {
  switch (s) {
    case 'Pending':
      return 'Chờ duyệt';
    case 'Answering':
      return 'AI đang viết';
    case 'Ready':
      return 'Chờ phát';
    case 'Played':
      return 'Đã lên live';
    case 'Rejected':
      return 'Bị từ chối';
    case 'Expired':
      return 'Quá hạn';
    case 'Failed':
      return 'Chưa phát được';
    default:
      return s;
  }
};

/**
 * Phần tử đầu tiên của `items` chưa có id trong `shownIds` (overlay tự phát lần lượt, nhớ id
 * đã phát để không lặp lại). `items` rỗng hoặc mọi phần tử đều đã hiện ⇒ null.
 */
export const nextToShow = <T extends { id: string }>(
  items: T[],
  shownIds: Set<string>
): T | null => items.find((item) => !shownIds.has(item.id)) ?? null;

const SENTENCE_END_CHARS = ['.', '?', '!'];

/**
 * Câu đọc to gộp người hỏi + câu hỏi + câu trả lời cho phần chèn overlay. M5/M6: câu hỏi thường
 * đã có dấu kết câu riêng ("… chưa?") — chỉ thêm "." khi câu hỏi CHƯA kết thúc bằng . ? !, tránh
 * dính "?." / "!." / ".." trước "Trả lời:".
 */
export const spokenQa = (askerName: string, question: string, answer: string): string => {
  const trimmedQuestion = question.trim();
  const alreadyPunctuated =
    trimmedQuestion.length > 0 && SENTENCE_END_CHARS.includes(trimmedQuestion.slice(-1));
  const questionPart = alreadyPunctuated ? trimmedQuestion : `${trimmedQuestion}.`;
  return `${askerName} hỏi: ${questionPart} Trả lời: ${answer}`;
};

// ── Nhịp hiện dần câu trả lời + ẩn dự phòng khi TTS lỗi (phần chèn) ─────────────────────

const ANSWER_REVEAL_MIN_MS = 1200;
const ANSWER_REVEAL_MAX_MS = 5000;
const ANSWER_REVEAL_MS_PER_CHAR = 25;
/** TTS công khai lỗi (mạng/máy chủ): vẫn chờ đủ để đọc hết chữ hiện dần rồi mới ẩn. */
const TTS_FALLBACK_BUFFER_MS = 4000;

/** Câu trả lời dài bao nhiêu thì hiện dần trong bấy nhiêu mili giây (kẹp 1.2–5 giây). */
export const revealDurationMs = (answerLength: number): number => {
  const len = Math.max(0, answerLength);
  return Math.min(ANSWER_REVEAL_MAX_MS, Math.max(ANSWER_REVEAL_MIN_MS, len * ANSWER_REVEAL_MS_PER_CHAR));
};

/**
 * Sau `elapsedMs` kể từ lúc bắt đầu hiện, câu trả lời (dài `answerLength` ký tự) đã hiện được
 * bao nhiêu ký tự, trong tổng thời lượng `durationMs` (từ `revealDurationMs`).
 */
export const revealedAnswerLength = (answerLength: number, elapsedMs: number, durationMs: number): number => {
  const len = Math.max(0, answerLength);
  if (durationMs <= 0 || elapsedMs >= durationMs) return len;
  if (elapsedMs <= 0) return 0;
  return Math.floor((len * elapsedMs) / durationMs);
};

/** TTS công khai không đọc được (lỗi mạng, không có âm thanh): ẩn thẻ sau khoảng này thay vì kẹt mãi. */
export const ttsFallbackHideMs = (answerLength: number): number =>
  revealDurationMs(answerLength) + TTS_FALLBACK_BUFFER_MS;

// ── I2: cắt câu đọc dài để gọi TTS công khai (giới hạn 500 ký tự/lượt — xem PublicTtsController) ──

/** Mỗi đoạn gọi TTS tối đa bấy nhiêu ký tự — chừa biên an toàn dưới mức 500 của PublicTtsController. */
export const TTS_CHUNK_MAX = 480;

/**
 * Cắt `text` thành các đoạn ≤ `max` ký tự để gọi TTS công khai lần lượt (I2: `spokenQa` cho câu
 * hỏi + câu trả lời đầy đủ thường vượt 500 ký tự, TTS công khai cắt cụt giữa câu). Ưu tiên cắt
 * ngay sau dấu kết câu (. ? !) gần cuối đoạn, không có thì cắt ở khoảng trắng gần cuối đoạn, không
 * bao giờ cắt giữa một từ (trừ khi một "từ" tự nó đã dài hơn `max`, trường hợp cực hiếm). Chuỗi
 * rỗng/toàn khoảng trắng ⇒ mảng rỗng. Chuỗi vừa đủ ⇒ mảng 1 phần tử (không chia khi không cần).
 */
export const chunkForTts = (text: string, max: number = TTS_CHUNK_MAX): string[] => {
  const normalized = (text ?? '').replace(/\s+/g, ' ').trim();
  if (!normalized) return [];
  if (normalized.length <= max) return [normalized];

  const chunks: string[] = [];
  let rest = normalized;
  while (rest.length > max) {
    const window = rest.slice(0, max);
    let cut = -1;
    for (let i = window.length - 1; i >= 0; i -= 1) {
      if (SENTENCE_END_CHARS.includes(window[i])) {
        cut = i + 1;
        break;
      }
    }
    if (cut === -1) {
      const lastSpace = window.lastIndexOf(' ');
      cut = lastSpace > 0 ? lastSpace : max; // không có khoảng trắng nào (1 "từ" siêu dài) ⇒ đành cắt cứng.
    }
    const piece = rest.slice(0, cut).trim();
    if (piece) chunks.push(piece);
    rest = rest.slice(cut).trim();
  }
  if (rest) chunks.push(rest);
  return chunks;
};

// ── I3: không đọc lại câu cũ sau khi overlay mở/tải lại (OBS restart, thêm nguồn giữa stream) ──

/**
 * Lần tải THÀNH CÔNG ĐẦU TIÊN sau khi overlay mở (hoặc đổi kênh/key): mọi câu đã có sẵn trong
 * `items` lúc đó coi như đã phát rồi — đánh dấu "đã hiện" (shown) mà KHÔNG đọc lại, để OBS mở lại
 * nguồn / tải lại trang không đọc to lại toàn bộ tối đa 10 phút câu hỏi đã qua. Câu đến SAU lần
 * tải đầu tiên đó (có id chưa từng thấy) vẫn phát bình thường như cũ.
 */
export const seedShownIdsOnFirstLoad = <T extends { id: string }>(items: T[]): Set<string> =>
  new Set(items.map((item) => item.id));
